import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { formatInTimeZone } from 'date-fns-tz';
import { db } from '../firebase/config';

const COLLECTION_NAME = 'contractions';
const STATS_COLLECTION = 'contractionsStats';
const IST_TIMEZONE = 'Asia/Kolkata';

/** Get the IST hour (0-23) from a JS Date */
const getISTHour = (date) =>
  parseInt(formatInTimeZone(date, IST_TIMEZONE, 'HH'), 10);

// ─── Hourly stats helpers ────────────────────────────────────────────────────

/**
 * Increment a specific hour bucket in contractionsStats/{userId}.
 * Creates the document if it doesn't exist.
 */
const incrementHourStats = async (userId, hour, durationSec) => {
  try {
    const statsRef = doc(db, STATS_COLLECTION, userId);
    const snap = await getDoc(statsRef);

    if (snap.exists()) {
      const currentHour = snap.data().hours?.[hour] || { total: 0, count: 0 };
      await updateDoc(statsRef, {
        [`hours.${hour}`]: {
          total: currentHour.total + durationSec,
          count: currentHour.count + 1,
        },
        updatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(statsRef, {
        userId,
        hours: {
          [hour]: { total: durationSec, count: 1 },
        },
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error incrementing hour stats:', error);
    // Non-fatal — don't block the main write
  }
};

/**
 * Decrement a specific hour bucket in contractionsStats/{userId}.
 * Removes the bucket if count reaches 0.
 */
const decrementHourStats = async (userId, hour, durationSec) => {
  try {
    const statsRef = doc(db, STATS_COLLECTION, userId);
    const snap = await getDoc(statsRef);
    if (!snap.exists()) return;

    const currentHour = snap.data().hours?.[hour];
    if (!currentHour) return;

    const newCount = currentHour.count - 1;
    const newTotal = currentHour.total - durationSec;

    if (newCount <= 0) {
      // Remove the bucket entirely
      const updatedHours = { ...snap.data().hours };
      delete updatedHours[hour];
      await updateDoc(statsRef, {
        hours: updatedHours,
        updatedAt: serverTimestamp(),
      });
    } else {
      await updateDoc(statsRef, {
        [`hours.${hour}`]: { total: newTotal, count: newCount },
        updatedAt: serverTimestamp(),
      });
    }
  } catch (error) {
    console.error('Error decrementing hour stats:', error);
  }
};

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Add a new contraction record and update hourly stats.
 * @param {string} userId - User email
 * @param {Object} data - { endTime: Date, durationSec: number }
 * @returns {Promise<string>} New document ID
 */
export const addContraction = async (userId, { endTime, durationSec }) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION_NAME), {
      userId,
      endTime,
      durationSec,
      createdAt: serverTimestamp(),
    });
    // Update aggregated hourly stats (non-blocking intent, errors are swallowed)
    await incrementHourStats(userId, getISTHour(endTime), durationSec);
    return docRef.id;
  } catch (error) {
    console.error('Error adding contraction:', error);
    throw error;
  }
};

/**
 * Fetch contractions for a user, newest first (used for the Records table).
 * @param {string} userId - User email
 * @returns {Promise<Array>}
 */
export const getContractions = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId),
      orderBy('endTime', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      endTime: d.data().endTime?.toDate(),
    }));
  } catch (error) {
    console.error('Error fetching contractions:', error);
    throw error;
  }
};

/**
 * Fetch pre-aggregated hourly stats — single document read.
 * Returns array of { hour, avg, count } sorted by hour.
 * @param {string} userId - User email
 * @returns {Promise<Array>}
 */
export const getHourlyStats = async (userId) => {
  try {
    const statsRef = doc(db, STATS_COLLECTION, userId);
    const snap = await getDoc(statsRef);
    if (!snap.exists()) return [];

    const hours = snap.data().hours || {};
    return Object.entries(hours)
      .map(([hour, { total, count }]) => ({
        hour: parseInt(hour, 10),
        avg: Math.round(total / count),
        count,
      }))
      .sort((a, b) => a.hour - b.hour);
  } catch (error) {
    console.error('Error fetching hourly stats:', error);
    throw error;
  }
};

/**
 * Delete a single contraction and decrement its hour bucket in stats.
 * @param {string} id - Firestore document ID
 * @param {string} userId - User email
 * @param {Date}   endTime - End time of the contraction
 * @param {number} durationSec - Duration in seconds
 * @returns {Promise<void>}
 */
export const deleteContraction = async (id, userId, endTime, durationSec) => {
  try {
    await deleteDoc(doc(db, COLLECTION_NAME, id));
    await decrementHourStats(userId, getISTHour(endTime), durationSec);
  } catch (error) {
    console.error('Error deleting contraction:', error);
    throw error;
  }
};

/**
 * Delete all contractions for a user and wipe their stats document.
 * @param {string} userId - User email
 * @returns {Promise<void>}
 */
export const deleteAllContractions = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);

    const batch = writeBatch(db);
    snapshot.docs.forEach((d) => batch.delete(d.ref));
    // Also clear the stats document
    batch.delete(doc(db, STATS_COLLECTION, userId));
    await batch.commit();
  } catch (error) {
    console.error('Error deleting all contractions:', error);
    throw error;
  }
};
